import type { CSSProperties, ReactNode } from "react";
import { DateRangeFilter } from "../DateRangeFilter";
import { useTranslation } from "react-i18next";
import type { OfferStage } from "../../api/offerStages";
import { activeStages } from "../../pages/offersBoard";
import {
  EMPTY_COUNTRY, type OfferFilterCriteria, type OwnerOption,
} from "../../pages/offersFilter";
import { countryName, flagEmoji } from "../../i18n/countries";
import { Select } from "../Select";
import Ic from "../icons.jsx";

/**
 * Shared offers filter bar — search/stage/country/date-range+presets/owner,
 * plus a shown/total summary and "clear filters" action. Extracted from OffersPage.tsx and
 * OffersBoardPage.tsx, which had copy-pasted this section verbatim including
 * ~8 CSS-in-JS style constants and the `FilterField` helper. Both pages now
 * render this single component so they stay pixel-identical and any future
 * filter-bar change (a new criterion, a copy tweak) happens in one place.
 *
 * i18n keys are owned here (both call sites used the exact same
 * `offers.filter.*` keys already, so no prop needed for label text).
 *
 * `extra` is an escape hatch for a view-specific trailing field — the board
 * appends its "sort within column" dropdown there; the list passes nothing.
 */
export type OffersFilterBarProps = {
  criteria: OfferFilterCriteria;
  onSearchChange: (v: string) => void;
  onStageChange: (v: string) => void;
  onCountryChange: (v: string) => void;
  onOwnerChange: (v: string) => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  /** Full stage list (incl. archived) — filtered/sorted internally via activeStages(). */
  stages: OfferStage[];
  /** Distinct customerCountry codes present in the loaded list (see offersFilter.distinctCountries). */
  countries: string[];
  owners: OwnerOption[];
  showOwnerFilter: boolean;
  shownCount: number;
  totalCount: number;
  filtersActive: boolean;
  onClear: () => void;
  /** View-specific trailing field, e.g. the board's sort-within-column dropdown. */
  extra?: ReactNode;
  /**
   * Shows/hides the shown/total summary row — OffersPage only renders it
   * once offers have loaded and there's at least one (matching its
   * pre-extraction behavior exactly); OffersBoardPage doesn't need this
   * since it doesn't mount the whole bar until that's already true. Defaults
   * to true so board call sites can omit it.
   */
  showSummary?: boolean;
};


export function OffersFilterBar({
  criteria, onSearchChange, onStageChange, onCountryChange, onOwnerChange,
  onDateFromChange, onDateToChange,
  stages, countries, owners, showOwnerFilter, shownCount, totalCount, filtersActive, onClear, extra,
  showSummary = true,
}: OffersFilterBarProps) {
  const { t, i18n } = useTranslation();
  const openStages = activeStages(stages);

  return (
    <section style={card}>
      <div style={filterRow}>
        <FilterField label={t("offers.filter.search")}>
          <div style={{ position: "relative" }}>
            <span style={searchIcon}><Ic.search /></span>
            <input
              value={criteria.search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("offers.filter.searchPlaceholder")}
              className="input"
              style={{ width: 220, paddingLeft: 30 }}
            />
          </div>
        </FilterField>

        <FilterField label={t("offers.filter.stage")}>
          <Select<string>
            value={criteria.stageId}
            onChange={onStageChange}
            ariaLabel={t("offers.filter.stage")}
            style={{ width: 180 }}
            options={[
              { value: "", label: t("offers.filter.stageAll") },
              ...openStages.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        </FilterField>

        <FilterField label={t("offers.filter.country")}>
          <Select<string>
            value={criteria.country}
            onChange={onCountryChange}
            ariaLabel={t("offers.filter.country")}
            style={{ width: 180 }}
            options={[
              { value: "", label: t("offers.filter.countryAll") },
              ...countries.map((code) =>
                code === EMPTY_COUNTRY
                  ? { value: EMPTY_COUNTRY, label: "—" }
                  : { value: code, label: countryName(code, i18n.resolvedLanguage ?? "en"), left: flagEmoji(code) },
              ),
            ]}
          />
        </FilterField>

        <FilterField label={" "}>
          <DateRangeFilter
            from={criteria.dateFrom}
            to={criteria.dateTo}
            onChange={(od, doo) => { onDateFromChange(od); onDateToChange(doo); }}
          />
        </FilterField>

        {showOwnerFilter && (
          <FilterField label={t("offers.filter.owner")}>
            <Select<string>
              value={criteria.ownerId}
              onChange={onOwnerChange}
              ariaLabel={t("offers.filter.owner")}
              style={{ width: 180 }}
              options={[
                { value: "", label: t("offers.filter.ownerAll") },
                ...owners.map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          </FilterField>
        )}

        {extra}
      </div>

      {showSummary && (
        <div style={filterSummary}>
          <span style={{ color: "var(--ink-3)", fontSize: 12.5 }}>
            {t("offers.filter.shown", { shown: shownCount, total: totalCount })}
          </span>
          {filtersActive && (
            <button type="button" className="btn ghost sm" onClick={onClear}>
              <Ic.x /> {t("offers.filter.clear")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** Exported so a caller's `extra` slot content (e.g. the board's sort dropdown) can match this bar's field styling. */
export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

// ---- Shared styles -------------------------------------------------------
// `card` and `emptyState` are also reused by the pages themselves for other
// sections (results table wrapper, no-data placeholders) — exported so
// there's exactly one definition of each instead of two identical copies.

export const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  boxShadow: "var(--shadow-1)",
  padding: 18,
};
export const emptyState: CSSProperties = {
  padding: 32, textAlign: "center",
  background: "var(--surface-2)", border: "1px dashed var(--border-strong)", borderRadius: "var(--r-sm)",
  display: "flex", flexDirection: "column", alignItems: "center",
};
const filterRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" };
const filterSummary: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)",
};
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const searchIcon: CSSProperties = { position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", width: 15, height: 15, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none" };
